import { createContext, useContext, useState, useEffect, useCallback, useRef, type FC, type ReactNode } from 'react';
import type { DeviceInfo } from '../types';

// API URL for device status - always use production since RUT956 sends data there
const GPS_API_URL = 'https://grossistify-api.vercel.app';

// Polling interval in milliseconds (3 seconds for near real-time updates)
const POLL_INTERVAL = 3000;

// Offline threshold in seconds - device is considered offline if no data received within this time
// This should be slightly higher than the RUT956 "Data to Server" interval
// Default: 15 seconds for fast detection (assumes RUT956 sends every ~10 seconds)
const OFFLINE_THRESHOLD_SECONDS = 15;

// Extended device info with status change tracking
interface DeviceWithStatusTracking extends DeviceInfo {
  statusChangedAt?: string; // When the device went online/offline
  _minutesSinceLastSeen?: number;
}

interface DeviceContextType {
  devices: DeviceWithStatusTracking[];
  selectedDevice: DeviceWithStatusTracking | null;
  setSelectedDevice: (device: DeviceWithStatusTracking | null) => void;
  loading: boolean;
  error: string | null;
  lastUpdate: Date | null;
  refreshDevices: () => Promise<void>;
  dataSource: 'rms' | 'local';
  onlineCount: number;
}

const DeviceContext = createContext<DeviceContextType | undefined>(undefined);

export const useDevices = () => {
  const context = useContext(DeviceContext);
  if (!context) {
    throw new Error('useDevices must be used within a DeviceProvider');
  }
  return context;
};

interface DeviceProviderProps {
  children: ReactNode;
}

export const DeviceProvider: FC<DeviceProviderProps> = ({ children }) => {
  const [devices, setDevices] = useState<DeviceWithStatusTracking[]>([]);
  const [selectedDevice, setSelectedDevice] = useState<DeviceWithStatusTracking | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const [dataSource, setDataSource] = useState<'rms' | 'local'>('local');
  
  // Use refs to avoid stale closure issues
  const selectedDeviceRef = useRef<string | null>(null);
  const isMountedRef = useRef(true);

  // Update ref when selectedDevice changes
  useEffect(() => {
    selectedDeviceRef.current = selectedDevice?.deviceId ?? null;
  }, [selectedDevice]);

  const fetchDevices = useCallback(async () => {
    try {
      // Try RMS API first for real-time data from Teltonika RMS
      let data = null;
      
      try {
        const rmsResponse = await fetch(`${GPS_API_URL}/api/rms-device`);
        if (rmsResponse.ok) {
          const rmsData = await rmsResponse.json();
          if (rmsData.success && rmsData.devices?.length > 0) {
            data = rmsData;
            if (isMountedRef.current) {
              setDataSource('rms');
            }
          }
        }
      } catch (rmsErr) {
        console.log('RMS API not available, falling back to local');
      }
      
      // Fallback to local device-status API
      if (!data) {
        const response = await fetch(`${GPS_API_URL}/api/device-status?offlineThreshold=${OFFLINE_THRESHOLD_SECONDS}`);
        if (!response.ok) throw new Error('Failed to fetch device status');
        
        data = await response.json();
        if (isMountedRef.current) {
          setDataSource('local');
        }
      }
      
      if (data.success && isMountedRef.current) {
        const fetchedDevices = data.devices || [];
        const now = new Date();
        
        // Process devices - use statusChangedAt from backend (persisted in Redis)
        const processedDevices = fetchedDevices.map((device: DeviceInfo) => {
          // Use statusChangedAt from backend if available, otherwise fall back to lastSeen
          // The backend now tracks this in Redis, so it persists across page refreshes
          const statusChangedAt = (device as any).statusChangedAt || device.lastSeen;
          
          // Calculate time since last seen for display purposes
          const lastSeen = new Date(device.lastSeen);
          const diffMinutes = (now.getTime() - lastSeen.getTime()) / 60000;
          
          return {
            ...device,
            statusChangedAt,
            _minutesSinceLastSeen: diffMinutes
          };
        });
        
        setDevices(processedDevices);
        setLastUpdate(new Date());
        setError(null);
        
        // Update selected device if it exists
        const currentSelectedId = selectedDeviceRef.current;
        if (currentSelectedId) {
          const updatedDevice = processedDevices.find((d: DeviceWithStatusTracking) => d.deviceId === currentSelectedId);
          if (updatedDevice) {
            setSelectedDevice(updatedDevice);
          }
        }
      }
    } catch (err) {
      console.error('Error fetching devices:', err);
      if (isMountedRef.current) {
        setError('Kunde inte hämta enhetsstatus');
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, []);

  // Initial fetch and setup polling
  useEffect(() => {
    isMountedRef.current = true;
    
    // Initial fetch
    fetchDevices();
    
    // Setup polling with faster interval for real-time updates
    const interval = setInterval(fetchDevices, POLL_INTERVAL);
    
    return () => {
      isMountedRef.current = false;
      clearInterval(interval);
    };
  }, [fetchDevices]);

  // Calculate online count
  const onlineCount = devices.filter(d => d.isOnline).length;

  const value: DeviceContextType = {
    devices,
    selectedDevice,
    setSelectedDevice,
    loading,
    error,
    lastUpdate,
    refreshDevices: fetchDevices,
    dataSource,
    onlineCount
  };

  return (
    <DeviceContext.Provider value={value}>
      {children}
    </DeviceContext.Provider>
  );
};

export default DeviceContext;
